import type { Component } from 'vue';
import type { FileInfo } from '@puppycad/proto';
import type {
  SketchRectangleCommand,
  SketchCircleCommand,
  SketchArcCommand,
  SketchEllipseCommand,
  SketchSlotCommand,
  SketchLineCommand,
  ExtrudeCutDefinition,
  ExtrudeDirectionDefinition,
  ExtrudeFeatureKind,
  ExtrudeOperation,
  ExtrudeThinFeatureDefinition,
  Viewer,
  InsertComponentFixationMode,
  SketchEntityReferenceSelectionDisplayKind,
  CoverType,
} from '@puppycad/cad-core';
// 枚举既是值也是类型，必须使用普通 export，不能用 export type。
export {
  MoveComponentCollisionCheckScope,
  MoveComponentCollisionSelectionPhase,
  MoveComponentInputMode,
  MoveComponentOptionMode,
  MoveComponentRotateInputMode,
} from '@puppycad/cad-core';

export enum RectangleConstructionLines {
  None = 'none',
  Corners = 'corners',
  Midpoints = 'midpoints',
}

export interface RectangleOptions {
  mode: SketchRectangleCommand;
  forConstruction: boolean;
  constructionLines: RectangleConstructionLines;
}

export interface RectangleParameters {
  corners: Array<[number, number, number]>;
  center?: [number, number, number];
  corners2d?: Array<[number, number]>;
  center2d?: [number, number];
}

export interface CircleOptions {
  mode: SketchCircleCommand;
  forConstruction: boolean;
}

export interface CircleParameters {
  center?: [number, number, number];
  center2d?: [number, number];
  radius?: number;
}

export interface ArcOptions {
  mode: SketchArcCommand;
  forConstruction: boolean;
}

export interface ArcParameters {
  center?: [number, number, number];
  center2d?: [number, number];
  start?: [number, number, number];
  start2d?: [number, number];
  end?: [number, number, number];
  end2d?: [number, number];
  radius?: number;
  angle?: number;
}

export interface EllipseOptions {
  mode: SketchEllipseCommand;
  forConstruction: boolean;
  addConstructionLines: boolean;
}

export interface EllipseParameters {
  center?: [number, number, number];
  center2d?: [number, number];
  radiusX?: number;
  radiusY?: number;
  rotation?: number;
  start?: [number, number, number];
  start2d?: [number, number];
  end?: [number, number, number];
  end2d?: [number, number];
  startAngle?: number;
  endAngle?: number;
  arcAngle?: number;
  anchor1?: [number, number, number];
  anchor1_2d?: [number, number];
  anchor2?: [number, number, number];
  anchor2_2d?: [number, number];
}

export enum PolygonMode {
  Inscribed = 'inscribed',
  Circumscribed = 'circumscribed',
}

export interface PolygonOptions {
  sides: number;
  mode: PolygonMode;
  forConstruction: boolean;
}

export interface PolygonParameters {
  center?: [number, number, number];
  center2d?: [number, number];
  radius?: number;
  rotation?: number;
  sides?: number;
  mode?: PolygonMode;
}

export interface PointParameters {
  point?: [number, number, number];
  point2d?: [number, number];
}

export enum SlotLengthType {
  CenterCenter = 'center-center',
  Overall = 'overall',
}
export function isSketchCornerMode(command: string): command is SketchCornerMode {
  return Object.values(SketchCornerMode).includes(command as SketchCornerMode);
}
export enum SketchCornerMode {
  Fillet = 'sketch-fillet',
  Chamfer = 'sketch-chamfer',
}

export enum SketchChamferType {
  AngleDistance = 'angle-distance',
  DistanceDistance = 'distance-distance',
  EqualDistance = 'equal-distance',
}

export enum SketchTrimMode {
  Power = 'power',
  Corner = 'corner',
  TrimAwayInside = 'trim-away-inside',
  TrimAwayOutside = 'trim-away-outside',
  TrimToClosest = 'trim-to-closest',
}

export enum SketchTrimSingleEntityBehavior {
  Trim = 'trim',
  Extend = 'extend',
}

export interface SketchCornerEntityRef {
  objectId: string;
  segmentIndex: number | null;
}

export interface SketchTrimEntityRef {
  objectId: string;
  segmentIndex: number | null;
  hitPoint?: [number, number, number];
}

export interface SketchFilletOptions {
  radius: number;
  keepConstrainedCorners: boolean;
  dimensionEachFillet: boolean;
}

export interface SketchChamferOptions {
  chamferType: SketchChamferType;
  distance1: number;
  distance2: number;
  angleDeg: number;
}

export interface SketchTrimOptions {
  mode: SketchTrimMode;
  singleEntityBehavior: SketchTrimSingleEntityBehavior;
  keepTrimmedAsConstruction: boolean;
  ignoreConstructionGeometry: boolean;
}

export interface SlotOptions {
  mode: SketchSlotCommand;
  addDimensions: boolean;
  forConstruction: boolean;
  lengthType: SlotLengthType;
}

export interface SlotParameters {
  mode?: SketchSlotCommand;
  center?: [number, number, number];
  center2d?: [number, number];
  start?: [number, number, number];
  start2d?: [number, number];
  end?: [number, number, number];
  end2d?: [number, number];
  width?: number;
  centerLength?: number;
  overallLength?: number;
  displayLength?: number;
  radius?: number;
  angle?: number;
}

export enum StyleSplineMode {
  Bezier = 'bezier',
  BSpline3 = 'bspline-3',
  BSpline5 = 'bspline-5',
  BSpline7 = 'bspline-7',
}

export interface StyleSplineOptions {
  mode: StyleSplineMode;
  forConstruction: boolean;
  showCurvature: boolean;
  keepInternalContinuity: boolean;
}

export type StyleSplineParameterUpdate =
  | { type: 'point'; index: number; axis: 'x' | 'y'; value: number }
  | { type: 'mode'; mode: StyleSplineMode }
  | { type: 'rational'; value: boolean; resetWeights?: boolean }
  | { type: 'weight'; index: number; value: number }
  | { type: 'bezierDegree'; value: number };

export interface SplineOptions {
  mode?: string;
  forConstruction: boolean;
  showCurvature: boolean;
  keepInternalContinuity: boolean;
}

export type SplineParameterUpdate =
  | { type: 'point'; index: number; axis: 'x' | 'y'; value: number };

export enum LineDirection {
  AsSketched = 'as-sketched',
  Horizontal = 'horizontal',
  Vertical = 'vertical',
  Angle = 'angle',
}

export interface LineOptions {
  type: SketchLineCommand;
  direction: LineDirection;
  angle: number;
  forConstruction: boolean;
  infiniteLength: boolean;
}

export interface LineParameters {
  length?: number;
  angle?: number;
}

export type LineParameterUpdate =
  | { type: 'length'; value: number }
  | { type: 'angle'; value: number };

export type RectangleParameterUpdate =
  | { type: 'corner'; index: number; axis: 'x' | 'y'; value: number }
  | { type: 'center'; axis: 'x' | 'y'; value: number };

export type CircleParameterUpdate =
  | { type: 'center'; axis: 'x' | 'y'; value: number }
  | { type: 'radius'; value: number };

export type ArcParameterUpdate =
  | { type: 'center'; axis: 'x' | 'y'; value: number }
  | { type: 'start'; axis: 'x' | 'y'; value: number }
  | { type: 'end'; axis: 'x' | 'y'; value: number }
  | { type: 'radius'; value: number }
  | { type: 'angle'; value: number };

export type EllipseParameterUpdate =
  | { type: 'center'; axis: 'x' | 'y'; value: number }
  | { type: 'radiusX'; value: number }
  | { type: 'radiusY'; value: number }
  | { type: 'rotation'; value: number }
  | { type: 'startAngle'; value: number }
  | { type: 'endAngle'; value: number };

export type SlotParameterUpdate =
  | { type: 'center'; axis: 'x' | 'y'; value: number }
  | { type: 'displayLength'; value: number }
  | { type: 'width'; value: number }
  | { type: 'radius'; value: number }
  | { type: 'angle'; value: number };

export interface PropertyManagerActions {
  confirm?: boolean;
  cancel?: boolean;
  preview?: boolean;
  help?: boolean;
}

/**
 * PropertyManager 动作按钮解析函数。
 */
export type PropertyManagerActionsResolver = (
  context: PropertyManagerContext,
) => PropertyManagerActions | null | undefined;

export interface ExtrudeSelectedContourItem {
  regionId: string;
  contourKey?: string;
  sketchId: string;
  label: string;
  /** 列表展示类型：普通边、面或草图链。 */
  kind?: SketchEntityReferenceSelectionDisplayKind;
}

/**
 * 拉伸属性栏快照。
 */
export interface ExtrudePropertyDefinitionSnapshot {
  /** 当前特征语义类型。 */
  featureKind: ExtrudeFeatureKind;
  /** 当前操作类型。 */
  operation: ExtrudeOperation;
  /** 方向 1。 */
  direction1: ExtrudeDirectionDefinition;
  /** 方向 2。 */
  direction2: ExtrudeDirectionDefinition;
  /** 薄壁特征。 */
  thinFeature: ExtrudeThinFeatureDefinition;
  /** 是否合并结果。 */
  mergeResult: boolean;
  /** 所选轮廓输入框是否处于激活态。 */
  selectedContoursInputActive: boolean;
  /** 拉伸切除专属定义。 */
  cutOptions: ExtrudeCutDefinition | null;
}

/**
 * 拉伸 PropertyManager 状态。
 */
export interface ExtrudePropertyManagerState {
  /** 当前是否处于拉伸命令。 */
  active: boolean;
  /** 当前拉伸操作类型。 */
  operation: 'boss' | 'cut';
  /** 拉伸属性定义。 */
  definition: ExtrudePropertyDefinitionSnapshot;
  /** 所选轮廓。 */
  selectedContours: ExtrudeSelectedContourItem[];
  /** 当前是否允许提交。 */
  canSubmit: boolean;
  /** 预览错误。 */
  previewError: string | null;
}

export type RevolvePickerTarget = 'axis' | 'profile' | null;

export interface RevolveSelectedContourItem {
  id: string;
  label: string;
}

export interface RevolvePropertyManagerState {
  active: boolean;
  editingFeatureId: string | null;
  direction1Angle: number;
  direction2Enabled: boolean;
  direction2Angle: number;
  directionReversed: boolean;
  selectedContours: RevolveSelectedContourItem[];
  selectedAxisLabel: string | null;
  pickerTarget?: RevolvePickerTarget;
  canSubmit: boolean;
  previewError: string | null;
}

/**
 * Convert Entities 面板中的已选实体项。
 *
 * 设计说明：
 * 1. Task 2 只需要最小展示态，因此先收口为稳定的 `id + label`；
 * 2. 真正的实体引用描述、预览根节点等重型数据后续再由 workflow/SDK 向内扩展；
 * 3. PropertyManager 只依赖展示字段，避免 UI 提前耦合底层实现细节。
 */
export interface ConvertEntitiesSelectedItem {
  /** 列表项唯一标识。 */
  id: string;
  /** 列表展示名称。 */
  label: string;
}

/**
 * Convert Entities 最小命令会话状态。
 *
 * 设计说明：
 * 1. 当前阶段只承载命令是否激活、目标草图、本次会话已选项与提交可用性；
 * 2. 状态来源统一由宿主 workflow 托管，PropertyManager 只读消费；
 * 3. 历史引用记录不在 Convert Entities 会话面板中展示，避免把命令态面板扩成引用管理面板。
 */
export interface ConvertEntitiesState {
  /** 当前命令会话是否激活。 */
  active: boolean;
  /** 当前目标草图组 ID。 */
  targetSketchGroupId: string | null;
  /** 当前已选实体列表。 */
  selectedItems: ConvertEntitiesSelectedItem[];
  /**
   * 是否把转换后的目标实体创建为构造几何。
   *
   * 说明：对应 SolidWorks Convert Entities PropertyManager 的“作为构造线”。
   */
  asConstruction: boolean;
  /**
   * 是否启用草图链选择。
   *
   * 说明：该选项只影响后续选择解析，已进入列表的选择项仍按其选择意图保留。
   */
  selectChain: boolean;
  /** 是否把后续 inner Wire 成员边点击提升为完整环。 */
  innerLoopsOneByOne?: boolean;
  /** 当前是否允许执行“选择所有内环”。 */
  canSelectAllInnerLoops?: boolean;
  /** 当前是否正在解析 Face/Wire 边界。 */
  isResolvingFaceBoundaries?: boolean;
  /** 当前是否允许点击确认。 */
  canConfirm: boolean;
}

//------ 草图实体变换 UI 状态 开始------
/**
 * 草图实体变换命令枚举。
 *
 * 设计说明：
 * 1. 四个命令在 UI 层共享同一套状态协议；
 * 2. 具体几何操作后续由 cad-core controller 负责；
 * 3. 这里仅表达 PropertyManager 需要展示与切换的命令语义。
 */
export enum SketchEntitiesTransformCommand {
  /** 移动实体。 */
  Move = 'sketch-move-entities',
  /** 复制实体。 */
  Copy = 'sketch-copy-entities',
  /** 旋转实体。 */
  Rotate = 'sketch-rotate-entities',
  /** 镜向实体。 */
  Mirror = 'sketch-mirror-entities',
}

/**
 * 草图实体平移类命令输入模式枚举。
 */
export enum SketchEntitiesTransformInputMode {
  /** 通过起点和终点计算变换。 */
  FromTo = 'from-to',
  /** 通过草图平面 X/Y 目标坐标计算变换。 */
  XY = 'xy',
}

/**
 * 草图实体变换选择角色枚举。
 */
export enum SketchEntitiesTransformSelectionRole {
  /** 选择要变换的实体。 */
  Entities = 'entities',
  /** 选择起点。 */
  FromPoint = 'from-point',
  /** 选择终点。 */
  ToPoint = 'to-point',
  /** 选择旋转中心。 */
  RotationCenter = 'rotation-center',
  /** 选择镜向轴。 */
  MirrorAxis = 'mirror-axis',
}

//------ 移动实体保留几何关系 开始------
/**
 * 草图实体变换几何关系保留模式枚举。
 */
export enum SketchEntitiesTransformRelationRetentionMode {
  /** 保留现有几何关系。 */
  KeepRelations = 'keep-relations',
  /** 断开所选实体与未选实体之间的外部几何关系。 */
  BreakExternalRelations = 'break-external-relations',
}
//------ 移动实体保留几何关系 结束------

/**
 * 草图实体变换面板中的已选实体项。
 */
export interface SketchEntitiesTransformSelectedItem {
  /** 列表项唯一标识。 */
  id: string;
  /** 列表展示名称。 */
  label: string;
}

/**
 * 草图实体变换点参数展示项。
 */
export interface SketchEntitiesTransformPointItem {
  /** 点参数对象 ID；自由点可使用会话生成 ID。 */
  id: string;
  /** 点参数展示名称。 */
  label: string;
}

/**
 * 草图实体变换 PropertyManager 状态。
 *
 * 设计说明：
 * 1. 当前阶段是 UI 先行，状态允许由宿主注入占位值；
 * 2. 后续 cad-core controller 完成后，宿主只需把真实 controller 快照映射到该协议；
 * 3. 组件不保存 Three.js 对象，避免 Vue 响应式装饰 Object3D。
 */
export interface SketchEntitiesTransformState {
  /** 当前命令会话是否激活。 */
  active: boolean;
  /** 当前命令类型。 */
  command: SketchEntitiesTransformCommand | null;
  /** 平移类命令输入模式。 */
  inputMode: SketchEntitiesTransformInputMode;
  /** 当前正在等待视口选择的角色；null 表示命令正在等待数值输入而非视口选择。 */
  activeSelectionRole: SketchEntitiesTransformSelectionRole | null;
  /** 几何关系保留模式。 */
  relationRetentionMode: SketchEntitiesTransformRelationRetentionMode;
  /** 当前已选实体列表。 */
  selectedItems: SketchEntitiesTransformSelectedItem[];
  /** From/To 模式起点。 */
  fromPoint: SketchEntitiesTransformPointItem | null;
  /** From/To 模式终点。 */
  toPoint: SketchEntitiesTransformPointItem | null;
  /** 旋转中心点。 */
  rotationCenter: SketchEntitiesTransformPointItem | null;
  /** X/Y 模式目标 X 坐标。 */
  targetX: number | null;
  /** X/Y 模式目标 Y 坐标。 */
  targetY: number | null;
  /** 旋转角度，单位为度。 */
  angleDeg: number | null;
  /** 是否允许确认。 */
  canConfirm: boolean;
  /** 是否允许重复上一次变换。 */
  canRepeat: boolean;
}
//------ 草图实体变换 UI 状态 结束------

/**
 * PropertyManager 上下文
 * 用于决定不同管理器页面的可见性和行为
 */

export interface PropertyManagerContext {
  /**
   * 动态扩展字段。
   *
   * 设计说明：
   * 1. PropertyManager 走注册表机制，不同特征会在宿主层按需注入自己的上下文字段；
   * 2. 这里保留显式核心字段，同时允许特征模块按职责扩展自己的局部协议；
   * 3. 这样可以避免每新增一个特征都强耦合修改整份上下文字段矩阵。
   */
  [key: string]: any;
  activeCommand: string | null;
  drawingTool: string | null;
  currentDrawingCommand?: string | null;
  /**
   * 是否处于历史版本只读预览态。
   *
   * 说明：该状态由版本工作区统一下发给 PropertyManager，
   * 面板据此隐藏编辑入口，并由宿主层再次拦截提交动作。
   */
  readonlyPreview?: boolean;
  /**
   * 是否隐藏空状态提示。
   *
   * 设计说明：
   * 空状态是否应该展示属于具体命令/编辑上下文的判断，宿主只消费布尔结果，避免识别草图工具细节。
   */
  hideEmptyMessage?: boolean;
  isDrawingMode: boolean;
  isSketchActive: boolean;
  datumPlaneId: string | number | null;
  selectedObject: any | null;
  /** NaiveUI 确认弹窗实例，仅用于 PropertyManager 内部的纯 UI 确认动作。 */
  dialog?: {
    /** 创建确认弹窗。 */
    create: (options: Record<string, unknown>) => unknown;
  };
  /** 当前视口运行时。 */
  renderer?: Viewer | null;
  /**
   * 准备一次草图属性编辑的 before 快照。
   *
   * 设计说明：
   * 1. 草图属性面板会先直接修改运行时对象，再调用 finalize 统一提交；
   * 2. 因此必须在 setter 执行前捕获 before，否则 Undo 会落到上一条绘制事务；
   * 3. 这里只传入目标对象，不把 three.js 对象放入 Vue 响应式状态。
   */
  prepareSketchPropertyEdit?: (target?: unknown) => void;
  /**
   * 完成一次草图属性编辑。
   * 说明：
   * 1. 属性面板成功修改草图对象后调用；
   * 2. 底层会统一执行约束求解、草图 dirty/commit 与必要的视图刷新；
   * 3. 这里只暴露为上下文回调，避免各面板直接拼装草图提交链路。
   */
  finalizeSketchPropertyEdit?: (
    target: unknown,
    input?: {
      reason?: string;
      sourceObjectId?: string;
      /** 圆系属性编辑必须保持的目标半径（米）。 */
      radiusTarget?: number;
      /** 直线属性编辑必须保持的目标长度（米）。 */
      lineLengthTarget?: number;
      /** 绘制态修改长度时是否保持先绘制的起点。 */
      preserveLineStartPoint?: boolean;
    },
  ) => void;
  /**
   * 删除草图关系。
   * 说明：引用驱动关系会同步删除引用记录，普通关系仅删除 solver constraint。
   */
  removeSketchRelation?: (targetEntity: unknown, constraintId: number) => Promise<boolean>;
  clearSelection?: () => void;
  onCancel?: () => void;
  onConfirm?: () => void;
  onHelp?: () => void;
  onPreviewToggle?: () => void;
  /**
   * 面板内部主动切换当前操作命令（如移动/旋转互切），
   * 用于同步 activeCommand，使 PropertyManager 标题等响应式字段能实时刷新。
   */
  onSwitchActiveCommand?: (command: string) => void;
  extrudeState?: unknown;
  extrudeActive?: boolean;
  extrudeOperation?: string;
  extrudeDepth?: number;
  extrudeDirection?: number | 'normal' | 'reverse';
  extrudeMergeResult?: boolean;
  extrudeSelectedContours?: ExtrudeSelectedContourItem[];
  extrudeCanSubmit?: boolean;
  extrudePreviewError?: string | null;
  submitExtrude?: () => void | Promise<void>;
  cancelExtrude?: () => void;
  setExtrudeDepth?: (depth: number) => void;
  setExtrudeDirection?: (direction: number | 'normal' | 'reverse') => void;
  setExtrudeDirection1?: (value: ExtrudeDirectionDefinition) => void;
  /** 激活指定方向的终止目标选择。 */
  setExtrudeEndTargetInputActive?: (direction: 'direction1' | 'direction2' | null) => void;
  /** 终止目标行使用查看器既有选择与悬停。 */
  setExtrudeEndTargetVisual?: (direction: 'direction1' | 'direction2', mode: 'select' | 'hover' | 'clear') => void;
  setExtrudeDirection2?: (value: ExtrudeDirectionDefinition) => void;
  setExtrudeCutOptions?: (value: ExtrudeCutDefinition | null) => void;
  setExtrudeThinFeature?: (value: ExtrudeThinFeatureDefinition) => void;
  setExtrudeMergeResult?: (mergeResult: boolean) => void;
  setExtrudeSelectedContoursInputActive?: (active: boolean) => void;
  removeExtrudeSelectedContour?: (regionId: string) => void;
  highlightExtrudeSelectedContour?: (regionId: string | null) => void;
  revolveState?: RevolvePropertyManagerState;
  revolveActive?: boolean;
  revolveSelectedContours?: RevolveSelectedContourItem[];
  revolveSelectedAxisLabel?: string | null;
  revolveDirectionReversed?: boolean;
  revolveDirection1Angle?: number;
  revolveDirection2Enabled?: boolean;
  revolveDirection2Angle?: number;
  revolvePickerTarget?: RevolvePickerTarget;
  revolveCutState?: RevolvePropertyManagerState & {
    flipSideToCut?: boolean;
    cutFragments?: Array<{
      sourceSolidId: string;
      shapeKey: string;
      label: string;
      order: number;
      selected: boolean;
    }>;
    cutDeletedSolidIds?: string[];
    keepBodiesDialog?: {
      visible: boolean;
      selectionMode: 'all' | 'selected';
      draftSelectedShapeKeys: Record<string, string[]>;
    };
  };
  revolveCutActive?: boolean;
  submitRevolve?: () => void | Promise<void>;
  cancelRevolve?: () => void;
  activateRevolvePicker?: (target: Exclude<RevolvePickerTarget, null>) => void;
  clearRevolveSelectedAxis?: () => void;
  clearRevolveSelectedContours?: () => void;
  removeRevolveSelectedContour?: (id: string) => void;
  highlightRevolveSelectedAxis?: (enabled: boolean) => void;
  highlightRevolveSelectedContour?: (id: string | null) => void;
  setRevolveDirection1Angle?: (angle: number) => void;
  setRevolveDirection2Enabled?: (enabled: boolean) => void;
  setRevolveDirection2Angle?: (angle: number) => void;
  setRevolveDirectionReversed?: (reversed: boolean) => void;
  submitRevolveCut?: () => void | Promise<void>;
  cancelRevolveCut?: () => void;
  activateRevolveCutPicker?: (target: Exclude<RevolvePickerTarget, null>) => void;
  clearRevolveCutSelectedAxis?: () => void;
  clearRevolveCutSelectedContours?: () => void;
  removeRevolveCutSelectedContour?: (id: string) => void;
  highlightRevolveCutSelectedAxis?: (enabled: boolean) => void;
  highlightRevolveCutSelectedContour?: (id: string | null) => void;
  setRevolveCutDirection1Angle?: (angle: number) => void;
  setRevolveCutDirection2Enabled?: (enabled: boolean) => void;
  setRevolveCutDirection2Angle?: (angle: number) => void;
  setRevolveCutDirectionReversed?: (reversed: boolean) => void;
  setRevolveCutFlipSideToCut?: (enabled: boolean) => void;
  toggleRevolveCutFragment?: (sourceSolidId: string, shapeKey: string) => void;
  setRevolveCutKeepBodiesSelectionMode?: (mode: 'all' | 'selected') => void;
  toggleRevolveCutKeepBodiesDraftFragment?: (sourceSolidId: string, shapeKey: string) => void;
  confirmRevolveCutKeepBodiesDialog?: () => void;
  cancelRevolveCutKeepBodiesDialog?: () => void;
  /** 倒角模式。 */
  sketchCornerMode?: SketchCornerMode | null;
  /** 倒角是否激活。 */
  sketchCornerEnable: boolean;
  /** 设置倒角模式回调。 */
  setSketchCornerMode?: (mode: SketchCornerMode | null) => void;
  //------ 剪裁实体业务流程整理 开始------
  /** 剪裁模式。 */
  sketchTrimMode?: SketchTrimMode | null;
  /** 剪裁选项。 */
  sketchTrimOptions?: SketchTrimOptions;
  /** 当前选中的剪裁实体引用。 */
  selectedSketchTrimEntity?: SketchTrimEntityRef | { objectId: string; segmentIndex: number | null; hitPoint?: [number, number, number] } | null | unknown;
  ellipseOptions?: EllipseOptions;
  referenceAxisActive?: boolean;
  submitReferenceAxis?: () => void | Promise<void>;
  cancelReferenceAxis?: () => void;
  referencePlaneActive?: boolean;
  submitReferencePlane?: () => void | Promise<void>;
  cancelReferencePlane?: () => void;
  referencePointActive?: boolean;
  submitReferencePoint?: () => void | Promise<void>;
  cancelReferencePoint?: () => void;
  /** 装配体基准轴命令是否激活。 */
  assemblyReferenceAxisActive?: boolean;
  /** 提交装配体基准轴。 */
  submitAssemblyReferenceAxis?: () => void | Promise<void>;
  /** 取消装配体基准轴。 */
  cancelAssemblyReferenceAxis?: () => void;
  /** 装配体基准面命令是否激活。 */
  assemblyReferencePlaneActive?: boolean;
  /** 提交装配体基准面。 */
  submitAssemblyReferencePlane?: () => void | Promise<void>;
  /** 取消装配体基准面。 */
  cancelAssemblyReferencePlane?: () => void;
  /** 装配体基准点命令是否激活。 */
  assemblyReferencePointActive?: boolean;
  /** 提交装配体基准点。 */
  submitAssemblyReferencePoint?: () => void | Promise<void>;
  /** 取消装配体基准点。 */
  cancelAssemblyReferencePoint?: () => void;
  /** 设置剪裁模式回调。 */
  setSketchTrimMode?: (mode: SketchTrimMode | null) => void;
  /** 设置剪裁选项回调。 */
  setSketchTrimOptions?: (options: SketchTrimOptions) => void;
  //------ 剪裁实体业务流程整理 结束------
  //------ 等距实体业务流程 开始------
  /** 等距实体偏移距离。 */
  sketchOffsetDistance?: number;
  /** 等距实体是否反向。 */
  sketchOffsetReverse?: boolean;
  /** 等距实体是否双向。 */
  sketchOffsetBidirectional?: boolean;
  /** 等距实体是否添加尺寸。 */
  sketchOffsetAddSize?: boolean;
  /** 等距实体是否开启选择链。 */
  sketchOffsetSelectChain?: boolean;
  /** 等距实体盖子配置。 */
  sketchOffsetCover?: { hasCover: boolean; type: CoverType };
  /** 等距实体是否为基本几何体。 */
  sketchOffsetBasicGeometry?: boolean;
  /** 等距实体是否为偏移几何体。 */
  sketchOffsetOffsetGeometry?: boolean;
  /** 设置等距实体距离回调。 */
  setSketchOffsetDistance?: (value: number) => void;
  /** 设置等距实体方向回调。 */
  setSketchOffsetReverse?: (value: boolean) => void;
  /** 设置等距实体双向回调。 */
  setSketchOffsetBidirectional?: (value: boolean) => void;
  /** 设置等距实体添加尺寸回调。 */
  setSketchOffsetAddSize?: (value: boolean) => void;
  /** 设置等距实体选择链回调。 */
  setSketchOffsetSelectChain?: (value: boolean) => void;
  /** 设置等距实体盖子配置回调。 */
  setSketchOffsetCover?: (value: { hasCover: boolean; type: CoverType }) => void;
  /** 设置等距实体基本几何体回调。 */
  setSketchOffsetBasicGeometry?: (value: boolean) => void;
  /** 设置等距实体偏移几何体回调。 */
  setSketchOffsetOffsetGeometry?: (value: boolean) => void;
  /** 清除等距实体工作流，退出模式。 */
  clearSketchOffsetWorkflow?: () => void;
  //------ 等距实体业务流程 结束------
  /** Convert Entities 命令会话状态。 */
  convertEntitiesState?: ConvertEntitiesState;
  /** 确认 Convert Entities。 */
  confirmConvertEntities?: () => void | Promise<void>;
  /** 取消 Convert Entities。 */
  cancelConvertEntities?: () => void;
  /** 移除单个 Convert Entities 已选实体。 */
  removeConvertEntitiesSelection?: (selectionId: string) => void;
  /** 设置 Convert Entities 是否作为构造线创建。 */
  setConvertEntitiesAsConstruction?: (value: boolean) => void;
  /** 设置 Convert Entities 是否启用草图链选择。 */
  setConvertEntitiesSelectChain?: (value: boolean) => void;
  /** 设置 Convert Entities 是否逐个选择内环。 */
  setConvertEntitiesInnerLoopsOneByOne?: (value: boolean) => void;
  /** 选择当前已选面的全部内环。 */
  selectAllConvertEntitiesInnerLoops?: () => void | Promise<void>;
  //------ 草图实体变换 UI 状态 开始------
  /** 草图实体变换命令会话状态。 */
  sketchEntitiesTransformState?: SketchEntitiesTransformState;
  /** 设置草图实体变换输入模式。 */
  setSketchEntitiesTransformInputMode?: (mode: SketchEntitiesTransformInputMode) => void;
  /** 激活草图实体变换选择角色。 */
  setSketchEntitiesTransformSelectionRole?: (role: SketchEntitiesTransformSelectionRole) => void;
  /** 设置草图实体变换 X/Y 目标坐标。 */
  setSketchEntitiesTransformTargetCoordinate?: (axis: 'x' | 'y', value: number | null) => void;
  /** 设置草图实体旋转角度。 */
  setSketchEntitiesTransformAngle?: (value: number | null) => void;
  /** 同步草图实体变换命令会话状态。 */
  syncSketchEntitiesTransformState?: (state: SketchEntitiesTransformState) => void;
  /** 移除单个草图实体变换已选实体。 */
  removeSketchEntitiesTransformSelection?: (selectionId: string) => void;
  /** 重复上一次草图实体变换。 */
  repeatSketchEntitiesTransform?: () => void;
  /** 确认草图实体变换命令。 */
  confirmSketchEntitiesTransform?: () => void | Promise<void>;
  /** 取消草图实体变换命令。 */
  cancelSketchEntitiesTransform?: () => void;
  //------ 草图实体变换 UI 状态 结束------
  /** 线绘制点数量。 */
  lineDrawingPointCount?: number;
  /** 绘制点数量。 */
  drawingPointCount?: number;
  /** 多实体关系面板候选态是否激活。 */
  multipleEntitiesBoxSelected?: boolean;
  /** 框选到的多个物体对象列表。 */
  multipleSelectedObjects?: unknown[];
  /** 框选到的多个物体 ID 列表。 */
  multipleSelectedObjectIds?: string[];
  /** 是否为 crossing 框选（右往左）。 */
  multipleSelectedIsCrossing?: boolean;
  /** 线属性面板是否可见。 */
  linePropertyVisible?: boolean;
  /** 圆弧属性面板是否可见。 */
  arcPropertyVisible?: boolean;
  /** 圆属性面板是否可见。 */
  circlePropertyVisible?: boolean;
  /** 椭圆族属性面板是否可见，抛物线复用该入口。 */
  ellipsePropertyVisible?: boolean;
  /** 多边形属性面板是否可见。 */
  polygonPropertyVisible?: boolean;
  /** 矩形属性面板是否可见。 */
  rectanglePropertyVisible?: boolean;
  /** 样条曲线属性面板是否可见。 */
  splinePropertyVisible?: boolean;
  /** 样式样条曲线属性面板是否可见。 */
  styleSplinePropertyVisible?: boolean;
  /** 槽口属性面板是否可见。 */
  slotPropertyVisible?: boolean;
  /** 点属性面板是否可见。 */
  pointPropertyVisible?: boolean;
  /** 当前选中的草图尺寸（尺寸属性 section 可见性判据，属性数据由 section 从 renderer 自拉）。 */
  sketchDimensionSelection?: {sketchId: string; dimensionId: string} | null;
  /** 插入线条面板是否可见。 */
  insertLinePropertyVisible?: boolean;
  /** 插入零部件面板是否激活。 */
  insertComponentActive?: boolean;
  /** 插入零部件文件列表。 */
  insertComponentFiles?: FileInfo[];
  /** 插入零部件筛选类型（OR 匹配，空数组等价于不过滤）。 */
  insertComponentFilterKinds?: string[];
  /** 插入零部件搜索关键词。 */
  insertComponentQuery?: string;
  /** 插入零部件加载状态。 */
  insertComponentLoading?: boolean;
  /** 插入零部件是否有更多。 */
  insertComponentHasMore?: boolean;
  /** 插入零部件总数。 */
  insertComponentTotal?: number;
  /** 插入时固定/浮动零部件策略。 */
  insertComponentFixationMode?: InsertComponentFixationMode;
  /** 插入零部件 PropertyManager 中按勾选顺序保存的文件列表。 */
  selectedInsertComponentFiles?: FileInfo[];
  /** 设置插入零部件筛选类型（OR 匹配多个 kind）。 */
  setInsertComponentFilterKinds?: (kinds: string[]) => void;
  /** 设置插入零部件搜索关键词。 */
  setInsertComponentQuery?: (query: string) => void;
  /** 设置插入时固定/浮动零部件策略。 */
  setInsertComponentFixationMode?: (mode: InsertComponentFixationMode) => void;
  /** 加载更多插入零部件。 */
  loadMoreInsertComponents?: () => void;
  /** 选择插入零部件。 */
  selectInsertComponent?: (file: FileInfo) => void;
  /** 切换插入零部件勾选状态。 */
  toggleInsertComponentSelection?: (file: FileInfo) => void;
  /** 确认插入零部件勾选队列。 */
  confirmInsertComponentSelection?: () => boolean;
  /** 取消插入零部件。 */
  cancelInsertComponent?: () => void;

}

/**
 * PropertyManager 页面定义
 * 由各功能模块注册，支持按上下文动态显示
 */
export type PropertyManagerSectionTitle = string | ((context: PropertyManagerContext) => string);

export interface PropertyManagerSection {
  id: string;
  title: PropertyManagerSectionTitle;
  order?: number;
  isApplicable?: (context: PropertyManagerContext) => boolean;
  component: Component;
  actions?: PropertyManagerActions;
  /**
   * 按当前业务上下文动态解析动作按钮。
   *
   * 设计说明：
   * 宿主 PropertyManager 不应判断 `sketch-*`、`assembly-*` 等具体命令族。需要在命令态与选择态之间
   * 切换确认/取消按钮时，由对应 section 在注册协议中声明。
   */
  resolveActions?: PropertyManagerActionsResolver;
  /**
   * 是否独占 PropertyManager 显示。
   *
   * 设计说明：
   * 裁剪、转换实体、装配配合等命令会话激活后只应展示自身面板。该能力必须由 section 注册声明，
   * 宿主不能硬编码具体业务 ID，否则后续新增命令会不断污染 PropertyManager 主组件。
   */
  exclusive?: boolean;
  /**
   * 独占分组标识。
   *
   * 设计说明：
   * 预留给多个互斥命令共享一组显示策略；当前没有分组需求时使用 `exclusive` 即可。
   */
  exclusiveGroup?: string;
  /**
   * 解析当前 section 的确认按钮禁用状态。
   *
   * 设计说明：
   * 1. 不同命令的可提交条件应由各自 section 或控制器判断，宿主 PropertyManager 只消费布尔结果；
   * 2. 该入口避免宿主硬编码具体业务 controller，保持属性栏与装配体 Mate 等扩展能力解耦。
   *
   * @param context 当前 PropertyManager 上下文。
   * @returns 返回 `true` 表示禁用确认按钮，返回 `false` 表示允许确认。
   */
  resolveConfirmDisabled?: (context: PropertyManagerContext) => boolean;
  /**
   * 订阅当前 section 会影响确认按钮状态的变更。
   *
   * 设计说明：
   * 宿主只需要知道“状态可能变化了”，不接收 Three.js 对象或业务控制器实例，避免 Vue 响应式系统代理
   * 渲染核心对象，也避免把业务状态协议散落到 PropertyManager 主组件中。
   *
   * @param context 当前 PropertyManager 上下文。
   * @param listener 状态变化监听器。
   * @returns 取消订阅函数；无订阅需求时可返回空。
   */
  subscribeConfirmState?: (context: PropertyManagerContext, listener: () => void) => (() => void) | void;
  onConfirm?: (context: PropertyManagerContext) => void;
  onCancel?: (context: PropertyManagerContext) => void;
  onHelp?: (context: PropertyManagerContext) => void;
  onPreviewToggle?: (context: PropertyManagerContext) => void;
}
